import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Code, Database, Globe, Smartphone, Brain, Settings, BookOpen, Star } from 'lucide-react';

const Tutorials = () => {
  const tutorialCategories = [
    {
      title: "Programming Languages",
      icon: Code,
      color: "bg-blue-500",
      tutorials: [
        { name: "Java", link: "/tutorial/java", popularity: "Most Popular" },
        { name: "Python", link: "/tutorial/python", popularity: "Trending" },
        { name: "C++", link: "/tutorial/cpp", popularity: "Popular" },
        { name: "JavaScript", link: "/tutorial/javascript", popularity: "Most Popular" },
        { name: "C", link: "/tutorial/c", popularity: "Fundamental" },
        { name: "C#", link: "/tutorial/csharp", popularity: "Popular" },
        { name: "PHP", link: "/tutorial/php", popularity: "Web Development" },
        { name: "Go", link: "/tutorial/go", popularity: "Growing" },
      ]
    },
    {
      title: "Web Technologies",
      icon: Globe,
      color: "bg-green-500",
      tutorials: [
        { name: "HTML", link: "/tutorial/html", popularity: "Fundamental" },
        { name: "CSS", link: "/tutorial/css", popularity: "Essential" },
        { name: "React", link: "/tutorial/react", popularity: "Most Popular" },
        { name: "Angular", link: "/tutorial/angular", popularity: "Popular" },
        { name: "Vue.js", link: "/tutorial/vue", popularity: "Trending" },
        { name: "Node.js", link: "/tutorial/nodejs", popularity: "Backend" },
        { name: "Express.js", link: "/tutorial/express", popularity: "Framework" },
        { name: "TypeScript", link: "/tutorial/typescript", popularity: "Growing" },
      ]
    },
    {
      title: "Database",
      icon: Database,
      color: "bg-purple-500",
      tutorials: [
        { name: "SQL", link: "/tutorial/sql", popularity: "Essential" },
        { name: "MySQL", link: "/tutorial/mysql", popularity: "Popular" },
        { name: "PostgreSQL", link: "/tutorial/postgresql", popularity: "Advanced" },
        { name: "MongoDB", link: "/tutorial/mongodb", popularity: "NoSQL" },
        { name: "Oracle", link: "/tutorial/oracle", popularity: "Enterprise" },
        { name: "SQLite", link: "/tutorial/sqlite", popularity: "Lightweight" },
        { name: "Redis", link: "/tutorial/redis", popularity: "Caching" },
        { name: "Cassandra", link: "/tutorial/cassandra", popularity: "Big Data" },
      ]
    },
    {
      title: "Mobile Development",
      icon: Smartphone,
      color: "bg-orange-500",
      tutorials: [
        { name: "Android", link: "/tutorial/android", popularity: "Popular" },
        { name: "iOS", link: "/tutorial/ios", popularity: "Popular" },
        { name: "React Native", link: "/tutorial/react-native", popularity: "Cross-platform" },
        { name: "Flutter", link: "/tutorial/flutter", popularity: "Growing" },
        { name: "Kotlin", link: "/tutorial/kotlin", popularity: "Android Preferred" },
        { name: "Swift", link: "/tutorial/swift", popularity: "iOS Native" },
        { name: "Xamarin", link: "/tutorial/xamarin", popularity: "Microsoft" },
        { name: "Ionic", link: "/tutorial/ionic", popularity: "Hybrid" },
      ]
    },
    {
      title: "Data Science & ML",
      icon: Brain,
      color: "bg-red-500",
      tutorials: [
        { name: "Machine Learning", link: "/tutorial/ml", popularity: "Hot" },
        { name: "Deep Learning", link: "/tutorial/deep-learning", popularity: "Advanced" },
        { name: "Data Science", link: "/tutorial/data-science", popularity: "Popular" },
        { name: "TensorFlow", link: "/tutorial/tensorflow", popularity: "Google" },
        { name: "PyTorch", link: "/tutorial/pytorch", popularity: "Facebook" },
        { name: "Pandas", link: "/tutorial/pandas", popularity: "Data Analysis" },
        { name: "NumPy", link: "/tutorial/numpy", popularity: "Scientific" },
        { name: "Scikit-learn", link: "/tutorial/sklearn", popularity: "ML Library" },
      ]
    },
    {
      title: "DevOps & Cloud",
      icon: Settings,
      color: "bg-indigo-500",
      tutorials: [
        { name: "Docker", link: "/tutorial/docker", popularity: "Containerization" },
        { name: "Kubernetes", link: "/tutorial/kubernetes", popularity: "Orchestration" },
        { name: "AWS", link: "/tutorial/aws", popularity: "Cloud Leader" },
        { name: "Azure", link: "/tutorial/azure", popularity: "Microsoft Cloud" },
        { name: "Google Cloud", link: "/tutorial/gcp", popularity: "Google Cloud" },
        { name: "Jenkins", link: "/tutorial/jenkins", popularity: "CI/CD" },
        { name: "Git", link: "/tutorial/git", popularity: "Version Control" },
        { name: "Linux", link: "/tutorial/linux", popularity: "Operating System" },
      ]
    }
  ];

  return (
    <div className="pt-20 bg-gray-50 dark:bg-gray-900 min-h-screen">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="container-max py-12">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="text-5xl font-bold text-gray-900 dark:text-white mb-4">
              Learn Programming
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
              Master programming with our comprehensive tutorials. From basics to advanced concepts, 
              we cover everything you need to become a skilled developer.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Tutorials Grid */}
      <div className="container-max py-12">
        <div className="grid gap-8">
          {tutorialCategories.map((category, categoryIndex) => (
            <motion.div
              key={category.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: categoryIndex * 0.1 }}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden"
            >
              {/* Category Header */}
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center space-x-3">
                  <div className={`p-2 rounded-lg ${category.color} text-white`}>
                    <category.icon className="w-6 h-6" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                    {category.title}
                  </h2>
                </div>
              </div>

              {/* Tutorials Grid */}
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {category.tutorials.map((tutorial, index) => (
                    <Link
                      key={tutorial.name}
                      to={tutorial.link}
                      className="group block"
                    >
                      <motion.div
                        className="p-4 rounded-lg border border-gray-200 dark:border-gray-600 hover:border-primary-300 dark:hover:border-primary-600 hover:shadow-md transition-all duration-200 bg-gray-50 dark:bg-gray-700 group-hover:bg-white dark:group-hover:bg-gray-600"
                        whileHover={{ y: -2 }}
                        transition={{ duration: 0.2 }}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="font-semibold text-gray-900 dark:text-white group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                            {tutorial.name}
                          </h3>
                          <BookOpen className="w-4 h-4 text-gray-400 group-hover:text-primary-500" />
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                            tutorial.popularity === 'Most Popular' ? 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300' :
                            tutorial.popularity === 'Trending' ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300' :
                            tutorial.popularity === 'Popular' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300' :
                            'bg-gray-100 text-gray-700 dark:bg-gray-600 dark:text-gray-300'
                          }`}>
                            {tutorial.popularity}
                          </span>
                          {(tutorial.popularity === 'Most Popular' || tutorial.popularity === 'Trending') && (
                            <Star className="w-3 h-3 text-yellow-500 fill-current" />
                          )}
                        </div>
                      </motion.div>
                    </Link>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="mt-16 text-center bg-gradient-to-r from-primary-500 to-primary-700 rounded-xl p-12 text-white"
        >
          <h2 className="text-3xl font-bold mb-4">Ready to Start Learning?</h2>
          <p className="text-xl mb-8 opacity-90">
            Join thousands of students who have mastered programming with OSOPCoding
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/courses"
              className="bg-white text-primary-600 hover:bg-gray-100 font-semibold py-3 px-8 rounded-lg transition-colors duration-200"
            >
              Browse All Courses
            </Link>
            <Link
              to="/job-oriented-course"
              className="bg-primary-800 hover:bg-primary-900 text-white font-semibold py-3 px-8 rounded-lg transition-colors duration-200"
            >
              Job-Oriented Program
            </Link>
          </div>
        </motion.div>
      </div>

    </div>
  );
};

export default Tutorials; 